import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Link2, Play, Send, X } from 'lucide-react';
import type { ChatSummary, OutgoingItem } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { useChat } from '../chat/ChatContext.tsx';
import { ChatAvatar } from '../components/ChatAvatar.tsx';
import { ChatPicker } from '../components/ChatPicker.tsx';
import { formatFileSize } from '../lib/format.ts';
import { dismissNativeShare, fileFromShare } from '../lib/nativeShare.ts';
import { isLinkOnly, oversized, planShare, type SharedItem } from '../lib/share.ts';
import { readVideoFrame } from '../lib/videoFrame.ts';
import './ShareScreen.css';

/** A shared item once its bytes have been read off the device. */
interface ReadItem {
  shared: SharedItem;
  file: File;
  /** Media only: the tile, and for a clip the frame that stands in for it. */
  outgoing: OutgoingItem | null;
}

/**
 * «Отправить в чат» — where something shared into the app from outside lands.
 *
 * The one screen in the app whose whole reason is a preview. Everywhere else
 * what is being sent was chosen inside the app a moment ago and is still on
 * screen behind the send; here it arrived from another app entirely, and this is
 * the only place anybody can see what is about to go, and to whom, before it
 * does. Which is also why the recipient starts empty: deciding for somebody
 * where a photograph goes is the one mistake this screen exists to prevent.
 *
 * What it sends is what the same content would have become had it been picked
 * from inside the app: pictures and clips as one album — the caption being
 * whatever line came with them — everything else as a file message each, and a
 * bare line of text as text. The arithmetic is in lib/share.ts; this draws it.
 */
export function ShareScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { pendingShare, clearPendingShare, sendAlbum, sendFile, sendText } = useChat();

  const [read, setRead] = useState<ReadItem[] | null>(null);
  const [readFailed, setReadFailed] = useState(false);
  const [target, setTarget] = useState<ChatSummary | null>(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);

  const plan = useMemo(() => (pendingShare ? planShare(pendingShare) : null), [pendingShare]);

  // Nothing to be on: a reload of this URL, or a share already dealt with.
  useEffect(() => {
    if (!pendingShare) navigate('/', { replace: true });
  }, [pendingShare, navigate]);

  // The bytes, off the device and into Files the outbox can take. A clip's
  // frame is read the same way a picked one's is, so a shared video's tile is
  // the picture a picked video's tile would have been — and the re-encode the
  // outbox does to a picked clip happens to this one too, for free.
  useEffect(() => {
    if (!plan) return;
    let alive = true;
    const media = new Set(plan.media);
    void (async () => {
      try {
        const items = await Promise.all(
          [...plan.media, ...plan.files].map(async (shared): Promise<ReadItem> => {
            const file = await fileFromShare(shared);
            if (!media.has(shared)) return { shared, file, outgoing: null };
            if (file.type.toLowerCase().startsWith('image/')) {
              return {
                shared,
                file,
                outgoing: {
                  file,
                  kind: 'photo',
                  poster: null,
                  durationMs: null,
                  previewUrl: URL.createObjectURL(file),
                },
              };
            }
            const frame = await readVideoFrame(file);
            return {
              shared,
              file,
              outgoing: {
                file,
                kind: 'video',
                poster: frame.poster,
                durationMs: frame.durationMs,
                // A video handed to an <img> draws the browser's broken-image
                // glyph, which reads as failure rather than as «картинки нет»;
                // the tile draws its own placeholder instead.
                previewUrl: frame.poster ? URL.createObjectURL(frame.poster) : null,
              },
            };
          }),
        );
        if (alive) setRead(items);
        else {
          for (const item of items) {
            if (item.outgoing?.previewUrl) URL.revokeObjectURL(item.outgoing.previewUrl);
          }
        }
      } catch {
        if (alive) setReadFailed(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [plan]);

  // The screen owns these for as long as it is up; the outbox makes its own the
  // moment a send is handed over.
  useEffect(
    () => () => {
      for (const item of read ?? []) {
        if (item.outgoing?.previewUrl) URL.revokeObjectURL(item.outgoing.previewUrl);
      }
    },
    [read],
  );

  const cancel = useCallback(() => {
    clearPendingShare();
    // Back to whatever the material came from, having sent nothing — on
    // Android. On an iPhone there is no way back into another app, so there
    // this lands on the chat list instead.
    void dismissNativeShare();
    navigate('/', { replace: true });
  }, [clearPendingShare, navigate]);

  const tooBig = plan && user ? oversized(plan, user.maxUploadBytes) : [];

  async function send() {
    if (!plan || !target || !read || busy || tooBig.length > 0) return;
    setBusy(true);
    const media = read.filter((item) => item.outgoing !== null);
    const files = read.filter((item) => item.outgoing === null);

    if (media.length > 0) {
      await sendAlbum(
        { items: media.map((item) => item.outgoing!), caption: plan.text ?? '' },
        null,
        target.id,
      );
    }
    for (const item of files) await sendFile(item.file, null, target.id);
    // A line of its own only when there was no album for it to be the caption
    // of — one message per thing shared, as the album rule already says.
    if (plan.text && media.length === 0) await sendText(plan.text, null, undefined, target.id);

    clearPendingShare();
    navigate(`/chat/${target.id}`, { replace: true });
  }

  if (!plan) return null;

  if (picking) {
    return (
      <ChatPicker
        title="Отправить в чат"
        onPick={(chat) => {
          setTarget(chat);
          setPicking(false);
        }}
        onClose={() => setPicking(false)}
      />
    );
  }

  const media = read?.filter((item) => item.outgoing !== null) ?? [];
  const files = read?.filter((item) => item.outgoing === null) ?? [];

  return (
    <div className="app-frame settings-screen share-screen">
      <header className="settings-header settings-header-divided">
        <button
          type="button"
          className="icon-button settings-back"
          onClick={cancel}
          aria-label="Отменить"
        >
          <X size={20} />
        </button>
        <h2 className="settings-heading share-heading">Отправить в чат</h2>
      </header>

      <div className="share-body">
        {readFailed ? (
          <div className="share-note share-note-error">Не удалось прочитать то, чем поделились</div>
        ) : read === null ? (
          <div className="share-note">Читаем…</div>
        ) : (
          <>
            {media.length > 0 && (
              <section className="share-section">
                <h3 className="share-label">
                  {media.length === 1 ? 'Из галереи' : `Из галереи · ${media.length}`}
                </h3>
                <div className={media.length === 1 ? 'share-single' : 'share-grid'}>
                  {media.map((item, index) => (
                    <div className="share-tile" key={`${item.shared.path}:${index}`}>
                      {item.outgoing?.previewUrl ? (
                        <img src={item.outgoing.previewUrl} alt="" />
                      ) : (
                        <span className="share-tile-blank">
                          <Play size={20} />
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {files.length > 0 && (
              <section className="share-section">
                <h3 className="share-label">Файлы</h3>
                {files.map((item, index) => (
                  <div className="share-file" key={`${item.shared.path}:${index}`}>
                    <FileText size={18} />
                    <span className="share-file-name">{item.shared.name}</span>
                    <span className="share-file-size">{formatFileSize(item.shared.size)}</span>
                  </div>
                ))}
              </section>
            )}

            {plan.text && (
              <section className="share-section">
                <h3 className="share-label">
                  {media.length > 0 ? 'Подпись' : isLinkOnly(plan.text) ? 'Ссылка' : 'Текст'}
                </h3>
                {/* A link gets the card the canvas draws, minus the title it
                    asks for: nothing here fetches the page, and inventing a
                    heading would be worse than the address on its own. */}
                <div className={isLinkOnly(plan.text) ? 'share-link' : 'share-text'}>
                  {isLinkOnly(plan.text) && <Link2 size={16} />}
                  <span>{plan.text}</span>
                </div>
              </section>
            )}

            {plan.dropped > 0 && (
              <div className="share-note">
                Больше десяти за раз не отправить — {plan.dropped} не поместились.
              </div>
            )}
            {tooBig.length > 0 && user && (
              <div className="share-note share-note-error">
                {tooBig.length === 1
                  ? `«${tooBig[0]!.name}» больше ${formatFileSize(user.maxUploadBytes)}`
                  : `${tooBig.length} файла больше ${formatFileSize(user.maxUploadBytes)}`}
              </div>
            )}

            <section className="share-section">
              <h3 className="share-label">Кому</h3>
              {/* Empty until somebody says otherwise. The canvas draws this
                  state and its sentence at the bottom, and it is the whole
                  safety of the screen. */}
              <button type="button" className="share-target" onClick={() => setPicking(true)}>
                {target ? (
                  <>
                    <ChatAvatar chat={target} className="share-target-avatar" glyph={19} />
                    <span className="share-target-name">{target.title}</span>
                    <span className="share-target-change">Изменить</span>
                  </>
                ) : (
                  <>
                    <span className="share-target-empty">Выберите чат</span>
                    <span className="share-target-change">Выбрать</span>
                  </>
                )}
              </button>
            </section>
          </>
        )}
      </div>

      <div className="share-foot">
        <span className="share-hint">
          {target ? `Уйдёт в «${target.title}»` : 'Не выбран получатель'}
        </span>
        <button
          type="button"
          className="share-send"
          aria-label="Отправить"
          disabled={!target || read === null || busy || tooBig.length > 0}
          onClick={() => void send()}
        >
          <Send size={20} />
        </button>
      </div>
    </div>
  );
}
